import React, { useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useHierarchalReportDataFullParallel } from './data'

const DDPReportContext = React.createContext({})

export const useDDPReportContext = () => {
  return useContext(DDPReportContext)
}

const useReportManager = (pipeline, variables, hierarchy, concurrency, chunkSize) => {
  const [globalFilter, setGlobalFilter] = useState('')
  const [loadingProgress, setLoadingProgress] = useState(null)

  const dataQuery = useMemo(() => {
    return {
      pipeline: pipeline,
      variables: variables,
      hierarchy: hierarchy
    }
  }, [pipeline, variables, hierarchy])

  const handleProgress = useCallback((progress) => {
    setLoadingProgress(progress)
  }, [])

  // Use FULL dataset loader for client-side operations
  const { data, isLoading, isFetching, error } = useHierarchalReportDataFullParallel(dataQuery, handleProgress, concurrency, chunkSize)

  return useMemo(() => {
    return {
      pipeline,
      originalVariables: variables,
      hierarchy,
      data,
      isLoading,
      isFetching,
      error,
      globalFilter,
      setGlobalFilter,
      loadingProgress
    }
  }, [pipeline, variables, hierarchy, data, isLoading, isFetching, error, globalFilter, loadingProgress])
}

export function DDPReportProvider ({
  pipeline,
  variables,
  hierarchy,
  concurrency,
  chunkSize,
  children
}) {
  const reportManager = useReportManager(
    pipeline,
    variables,
    hierarchy,
    concurrency,
    chunkSize
  )

  return (
    <DDPReportContext.Provider value={reportManager}>
      {children}
    </DDPReportContext.Provider>
  )
}

DDPReportProvider.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  /** List of variables */
  variables: PropTypes.object,

  /** How to transform flat data into hierarchal */
  hierarchy: PropTypes.object,

  /** Number of concurrent chunk requests (default: 4, only for parallel) */
  concurrency: PropTypes.number,

  /** Rows per chunk (default: 10000) */
  chunkSize: PropTypes.number,

  children: PropTypes.node
}

DDPReportProvider.defaultProps = {
  concurrency: 4,
  chunkSize: 10000
}

export default DDPReportProvider
